/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/ModularForm.tsx
 * Release: 1.1.0 - Integrato sistema tab navigation
 * Descrizione: Form modulare con switch Azienda/Privato e animazioni
 */

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { FormConfig, FormGroup as FormGroupType, FormRow, FormColumn } from './types';
import { FormGroup } from './components/FormGroup';
import { ModernFormTypeSwitch, type FormType } from './components/ModernFormTypeSwitch';
// import { useTabNavigation } from '../forms/hooks/useTabNavigation';
import { validateForm } from './utils/validation';

interface ModularFormProps {
  config: FormConfig | { azienda: FormConfig; privato: FormConfig };
  onSubmit?: (values: Record<string, any>) => void | Promise<void>;
  onValuesChange?: (values: Record<string, any>) => void;
  onErrorsChange?: (errors: Record<string, string>) => void;
  initialValues?: Record<string, any>;
  userPermissions?: string[];
  showTypeSwitch?: boolean;
  defaultType?: 'azienda' | 'privato';
  onFormTypeChange?: (type: 'azienda' | 'privato') => void;
  className?: string;
  submitButtonText?: string;
  hideResetButton?: boolean;
  hideSubmitButton?: boolean;
  readOnly?: boolean;
  // Se true, non blocca il submit per campi required mancanti (mostra solo warning)
  skipRequiredValidation?: boolean;
  // Callback per gestire campi required mancanti (popup conferma)
  onMissingRequiredFields?: (missingFields: string[]) => Promise<boolean>;
  // Contenuto extra da mostrare nella parte sinistra della sezione Tipo Cliente
  typeSwitchLeftContent?: React.ReactNode;
  // Contenuto extra da mostrare sotto la sezione Tipo Cliente (es. QR code cliente)
  typeSwitchBottomContent?: React.ReactNode;
}

export const ModularForm: React.FC<ModularFormProps> = ({
  config,
  initialValues = {},
  onSubmit,
  onValuesChange,
  onErrorsChange,
  userPermissions = ['user'],
  className = '',
  showTypeSwitch = true,
  defaultType = 'azienda',
  onFormTypeChange,
  submitButtonText = 'Salva Dati',
  hideResetButton = true,
  hideSubmitButton = false,
  readOnly = false,
  skipRequiredValidation = false,
  onMissingRequiredFields,
  typeSwitchLeftContent,
  typeSwitchBottomContent
}) => {
  const [formType, setFormType] = useState<FormType>(defaultType);
  const [values, setValues] = useState<Record<string, any>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attemptedProgression, setAttemptedProgression] = useState<Set<string>>(new Set());
  const [openSections] = useState<Set<string>>(new Set());
  const formRef = useRef<HTMLFormElement>(null);

  // Ref per tracciare il defaultType precedente - inizializzato a null per garantire
  // che il primo sync avvenga sempre (null !== qualsiasi_valore)
  const prevDefaultTypeRef = useRef<FormType | null>(null);

  // Ref per bloccare useEffect INITIAL VALUES quando c'è un cambio tipo in corso
  // Questo evita che i vecchi initialValues sovrascrivano i nuovi defaults durante il cambio tipo
  const typeChangeInProgressRef = useRef<boolean>(false);

  // Sincronizza formType con defaultType quando defaultType cambia dall'esterno
  // Questo gestisce il cambio da DevSimulator
  React.useEffect(() => {
    const prevDefaultType = prevDefaultTypeRef.current;

    console.log(`🔄 [ModularForm] ========== useEffect SYNC TYPE ==========`);
    console.log(`🔄 [ModularForm] defaultType="${defaultType}", prevDefaultType="${prevDefaultType}", formType="${formType}"`);
    console.log(`🔄 [ModularForm] Condizione sync: prevDefaultType !== defaultType = ${prevDefaultType !== defaultType}`);
    console.log(`🔄 [ModularForm] readOnly: ${readOnly}`);

    // In modalità readOnly, non facciamo sync del tipo perché:
    // 1. Non ci sono cambi tipo manuali (switch disabled)
    // 2. Vogliamo mostrare i dati così come sono stati caricati
    // 3. Il reset dei values cancellerebbe i dati da visualizzare
    // 4. La chiave del form è ora statica ('readonly-form'), non serve cambiare formType
    if (readOnly) {
      console.log(`🔄 [ModularForm] ⏸️ SKIP SYNC TYPE: readOnly mode, solo visualizzazione`);
      // In readOnly mode, impostiamo formType solo UNA VOLTA per la configurazione corretta
      // senza triggerare re-render multipli (la chiave è statica)
      if (formType !== defaultType && prevDefaultTypeRef.current === null) {
        console.log(`🔄 [ModularForm] Prima inizializzazione formType in readOnly: ${formType} → ${defaultType}`);
        setFormType(defaultType);
      }
      prevDefaultTypeRef.current = defaultType;
      return;
    }

    // Sincronizza SOLO se defaultType è CAMBIATO (non al primo render con prevDefaultType=null)
    // oppure se è il primo render E defaultType != formType (per inizializzazione)
    const isFirstRender = prevDefaultType === null;
    const defaultTypeChanged = prevDefaultType !== null && prevDefaultType !== defaultType;
    const needsInitialSync = isFirstRender && defaultType !== formType;

    if (defaultTypeChanged || needsInitialSync) {
      console.log(`🔄 [ModularForm] ✅ SYNC TIPO: ${formType} → ${defaultType} (${isFirstRender ? 'initial sync' : 'type changed'})`);

      // BLOCCA useEffect INITIAL VALUES per evitare che i vecchi initialValues
      // sovrascrivano i nuovi defaults durante il cambio tipo
      typeChangeInProgressRef.current = true;
      // Reset il flag dopo che React ha processato tutti gli useEffect
      setTimeout(() => {
        typeChangeInProgressRef.current = false;
        console.log(`🔄 [ModularForm] typeChangeInProgress reset a false`);
      }, 0);

      // IMPORTANTE: Quando il tipo cambia dall'esterno (DevSimulator), dobbiamo:
      // 1. Cancellare i dati SessionStorage del VECCHIO tipo (potrebbero essere sporchi)
      // 2. Resettare i values a defaults per il nuovo tipo
      // 3. Cambiare formType

      const currentUserId = values.userId || 'current-user';

      // PULISCI SessionStorage per il NUOVO tipo per evitare che dati stale vengano caricati
      // da handleFormTypeChange o altri meccanismi
      const newTypeStorageKey = `formData_${currentUserId}_${defaultType}`;
      console.log(`🧹 [ModularForm] Pulisco SessionStorage: ${newTypeStorageKey}`);
      sessionStorage.removeItem(newTypeStorageKey);

      // Reset values con defaults per il nuovo tipo
      // FIX: Preserva paymentMethods/deliveryMethods da initialValues se esistono
      // Helper inline per gestire sia struttura normale che double-nested
      const hasMethodsData = (obj: any): boolean => {
        if (!obj) return false;
        if (Array.isArray(obj.methods) && obj.methods.length > 0) return true;
        if (obj.methods && typeof obj.methods === 'object' && Array.isArray(obj.methods.methods) && obj.methods.methods.length > 0) return true;
        return false;
      };
      const hasInitialPaymentData = hasMethodsData(initialValues.paymentMethods);
      const hasInitialDeliveryData = hasMethodsData(initialValues.deliveryMethods);

      const newTypeDefaults: Record<string, any> = {
        // Valori di default sempre presenti
        indirizzo: { search: '', via: '', numeroCivico: '', citta: '', cap: '', provincia: '', paese: 'Italia' },
        prefissoNazione: 'IT',
        prefissoTelefono: '+39',
        referentePrincipalePrefissoFisso: '+39',
        referentePrincipalePrefissoCellulare: '+39',
        referenteSecondarioPrefissoFisso: '+39',
        referenteSecondariePrefissoCellulare: '+39',
        meccanicoPrefissoTelefono: '+39',
        referentePrivatoPrefissoFisso: '+39',
        referentePrivatoPrefissoCellulare: '+39',
        customer_type: defaultType === 'azienda' ? 'company' : 'individual',

        // Oggetti vuoti per sezioni complesse
        social: { whatsapp: false, telegram: false },
        telefonoSocial: { whatsapp: false, telegram: false },
        referentePrincipaleSocial: { whatsapp: false, telegram: false },
        referenteSecondarioSocial: { whatsapp: false, telegram: false },
        referentePrivatoSocial: { whatsapp: false, telegram: false },
        meccanicoIndirizzo: {
          via: '',
          numeroCivico: '',
          cap: '',
          citta: '',
          provincia: '',
          paese: 'Italia'
        },
        // FIX: Preserva paymentMethods da initialValues se popolati
        paymentMethods: hasInitialPaymentData ? initialValues.paymentMethods : {
          methods: [],
          preferences: { notes: '' },
          notes: ''
        },
        // FIX: Preserva deliveryMethods da initialValues se popolati
        deliveryMethods: hasInitialDeliveryData ? initialValues.deliveryMethods : {
          methods: [],
          preferences: {
            preferredAddress: '',
            timeSlot: '',
            notes: ''
          },
          address: '',
          timeSlot: '',
          notes: ''
        },
        userId: currentUserId
      };

      console.log(`🧹 [ModularForm] Reset values per tipo esterno: ${defaultType}`, {
        hasInitialPaymentData,
        hasInitialDeliveryData,
        paymentMethods: newTypeDefaults.paymentMethods,
        deliveryMethods: newTypeDefaults.deliveryMethods
      });

      // Prima reset dei values, poi cambio formType
      setValues(newTypeDefaults);
      setErrors({});
      setAttemptedProgression(new Set());
      setFormType(defaultType);

      // NON chiamare onValuesChange qui - DevSimulator imposterà i valori corretti
      // via il suo meccanismo dopo il cambio tipo (con setTimeout 100ms)
    } else {
      console.log(`🔄 [ModularForm] Nessun sync necessario: defaultType="${defaultType}", prevDefaultType="${prevDefaultType}", formType="${formType}"`);
    }

    // SEMPRE aggiorna il ref al valore corrente di defaultType
    prevDefaultTypeRef.current = defaultType;
  }, [defaultType, readOnly]); // defaultType + readOnly per gestire modalità lettura

  // Calcola se ci sono solo errori required (per decidere se abilitare il pulsante)
  const getOnlyRequiredErrors = () => {
    const requiredErrors: Record<string, string> = {};
    const otherErrors: Record<string, string> = {};

    Object.entries(errors).forEach(([key, msg]) => {
      if (msg.toLowerCase().includes('obbligatorio') || msg.toLowerCase().includes('required')) {
        requiredErrors[key] = msg;
      } else {
        otherErrors[key] = msg;
      }
    });

    return { requiredErrors, otherErrors };
  };

  // Se skipRequiredValidation è true, il pulsante è disabilitato solo per errori di formato
  const { otherErrors } = getOnlyRequiredErrors();
  const hasBlockingErrors = skipRequiredValidation
    ? Object.keys(otherErrors).length > 0
    : Object.keys(errors).length > 0;



  // Aggiorna values quando initialValues cambia
  // NOTA: NON dipendere da formType qui - il cambio tipo è gestito dall'altro useEffect
  React.useEffect(() => {
    // Questo useEffect serve SOLO per sincronizzare con initialValues
    // Il caricamento da SessionStorage è gestito da handleFormTypeChange quando
    // l'utente cambia tipo manualmente, NON qui

    console.log('🔍 [ModularForm] ========== useEffect INITIAL VALUES ==========');
    console.log('🔍 [ModularForm] initialValues keys:', Object.keys(initialValues));
    console.log('🔍 [ModularForm] initialValues.customer_type:', initialValues.customer_type);
    console.log('🔍 [ModularForm] Current formType:', formType);
    console.log('🔍 [ModularForm] typeChangeInProgress:', typeChangeInProgressRef.current);
    console.log('🔍 [ModularForm] readOnly:', readOnly);

    // BLOCCA se c'è un cambio tipo in corso - i vecchi initialValues non devono
    // sovrascrivere i nuovi defaults impostati dall'useEffect SYNC TYPE
    // ECCEZIONE: In modalità readOnly, il form è solo lettura quindi ignoriamo il flag
    // perché non ci sono cambi tipo manuali e vogliamo sempre mostrare i dati
    if (typeChangeInProgressRef.current && !readOnly) {
      console.log('🔍 [ModularForm] ⚠️ SKIP: cambio tipo in corso, ignoro initialValues');
      return;
    }

    // Se initialValues ha dati, usali (sovrascrive tutto)
    if (Object.keys(initialValues).length > 0) {
      console.log('🔍 [ModularForm] ✅ Impostando values da initialValues');
      console.log('🔍 [ModularForm] initialValues.email:', initialValues.email);
      console.log('🔍 [ModularForm] initialValues.ragioneSociale:', initialValues.ragioneSociale);
      console.log('🔍 [ModularForm] initialValues.paymentMethods:', JSON.stringify(initialValues.paymentMethods));
      console.log('🔍 [ModularForm] initialValues.deliveryMethods:', JSON.stringify(initialValues.deliveryMethods));
      console.log('🔍 [ModularForm] initialValues.privacyAccepted:', initialValues.privacyAccepted);

      // FIX: Merge con i valori esistenti per preservare dati già caricati
      // e assicurarsi che paymentMethods/deliveryMethods non vengano persi
      setValues(prevValues => {
        // Helper per controllare se paymentMethods/deliveryMethods hanno dati
        // Gestisce sia struttura normale (methods: array) che double-nested (methods: {methods: array})
        const hasMethodsData = (obj: any): boolean => {
          if (!obj) return false;
          // Caso normale: methods è un array
          if (Array.isArray(obj.methods) && obj.methods.length > 0) return true;
          // Caso double-nested: methods è un oggetto con un array methods dentro
          if (obj.methods && typeof obj.methods === 'object' && Array.isArray(obj.methods.methods) && obj.methods.methods.length > 0) return true;
          return false;
        };

        // Helper per ottenere la lunghezza dei methods (per debug)
        const getMethodsLength = (obj: any): number => {
          if (!obj) return 0;
          if (Array.isArray(obj.methods)) return obj.methods.length;
          if (obj.methods && typeof obj.methods === 'object' && Array.isArray(obj.methods.methods)) return obj.methods.methods.length;
          return 0;
        };

        // Se prevValues ha già dati significativi (non solo defaults vuoti),
        // fai un merge intelligente invece di sovrascrivere tutto
        const hasPrevData = prevValues.email || prevValues.ragioneSociale ||
                           hasMethodsData(prevValues.paymentMethods) ||
                           hasMethodsData(prevValues.deliveryMethods);

        const hasNewPaymentData = hasMethodsData(initialValues.paymentMethods);
        const hasNewDeliveryData = hasMethodsData(initialValues.deliveryMethods);

        console.log('🔍 [ModularForm] Merge check:', {
          hasPrevData,
          hasNewPaymentData,
          hasNewDeliveryData,
          prevPaymentMethods: getMethodsLength(prevValues.paymentMethods),
          newPaymentMethods: getMethodsLength(initialValues.paymentMethods)
        });

        // Se i nuovi initialValues hanno dati per payment/delivery, usali
        // Altrimenti preserva quelli esistenti
        const mergedValues = {
          ...initialValues,
          // Preserva paymentMethods se già popolato e newValues non li ha
          paymentMethods: hasNewPaymentData
            ? initialValues.paymentMethods
            : (hasMethodsData(prevValues.paymentMethods)
                ? prevValues.paymentMethods
                : initialValues.paymentMethods),
          // Preserva deliveryMethods se già popolato e newValues non li ha
          deliveryMethods: hasNewDeliveryData
            ? initialValues.deliveryMethods
            : (hasMethodsData(prevValues.deliveryMethods)
                ? prevValues.deliveryMethods
                : initialValues.deliveryMethods),
        };

        console.log('🔍 [ModularForm] Final merged values - paymentMethods:', JSON.stringify(mergedValues.paymentMethods));
        console.log('🔍 [ModularForm] Final merged values - deliveryMethods:', JSON.stringify(mergedValues.deliveryMethods));

        return mergedValues;
      });

      // Verifica dopo il set
      setTimeout(() => {
        console.log('🔍 [ModularForm] AFTER setValues - verifico che values sia stato aggiornato');
      }, 100);
    }
    // Se initialValues è vuoto, NON caricare da SessionStorage qui
    // I defaults sono già stati impostati nel primo useEffect
  }, [initialValues, readOnly]);

  // Determina la configurazione corrente
  const getCurrentConfig = () => {
    console.log(`🚨 [ModularForm] DEBUG FORZATO - Tipo form: ${formType}`, {
      DEBUG_TIMESTAMP: new Date().toISOString(),
      configKeys: Object.keys(config)
    });

    if ('azienda' in config && 'privato' in config) {
      // Config con switch azienda/privato
      const currentConfig = config[formType];
      console.log(`🔧 [ModularForm] Caricata configurazione ${formType}:`, {
        groups: currentConfig.groups?.length,
        hasCustomFields: currentConfig.groups?.some(g => 
          g.rows?.some(r => 
            r.columns?.some(c => c.field.type === 'custom')
          )
        ),
        DEBUG_TIMESTAMP: new Date().toISOString()
      });
      return currentConfig;
    } else {
      // Config singolo
      console.log('🔧 [ModularForm] Caricata configurazione singola:', {
        groups: (config as FormConfig).groups?.length,
        hasCustomFields: (config as FormConfig).groups?.some(g => 
          g.rows?.some(r => 
            r.columns?.some(c => c.field.type === 'custom')
          )
        ),
        DEBUG_TIMESTAMP: new Date().toISOString()
      });
      return config as FormConfig;
    }
  };

  const currentConfig = getCurrentConfig();

  // Debug per verificare la configurazione corrente
  console.log(`🚨 [ModularForm] CONFIGURAZIONE CORRENTE:`, {
    formType,
    groups: currentConfig.groups?.length,
    firstGroup: currentConfig.groups?.[0]?.id,
    firstGroupRows: currentConfig.groups?.[0]?.rows?.length,
    firstRowColumns: currentConfig.groups?.[0]?.rows?.[0]?.columns?.length,
    firstColumn: currentConfig.groups?.[0]?.rows?.[0]?.columns?.[0],
    DEBUG_TIMESTAMP: new Date().toISOString()
  });

  // Debug per listare tutti i campi custom
  const customFields = currentConfig.groups?.flatMap(group => 
    group.rows?.flatMap(row => 
      row.columns?.filter(col => col.field.type === 'custom')
    ) || []
  ) || [];
  
  console.log(`🚨 [ModularForm] CAMPI CUSTOM TROVATI:`, {
    count: customFields.length,
    fields: customFields.map(field => ({
      id: field.id,
      label: field.field.label,
      customComponent: field.field.customComponent,
      type: field.field.type
    })),
    DEBUG_TIMESTAMP: new Date().toISOString()
  });

  // Debug dettagliato per ogni campo custom
  if (customFields.length > 0) {
    console.log(`🚨 [ModularForm] DETTAGLI CAMPI CUSTOM:`, {
      details: customFields.map(field => ({
        id: field.id,
        label: field.field.label,
        customComponent: field.field.customComponent,
        type: field.field.type,
        fieldObject: field.field
      })),
      DEBUG_TIMESTAMP: new Date().toISOString()
    });

    // Debug espanso per ogni campo custom
    console.log(`🚨 [ModularForm] CAMPI CUSTOM ESPANSI:`, {
      fields: customFields.map(field => ({
        id: field.id,
        label: field.field.label,
        customComponent: field.field.customComponent,
        type: field.field.type,
        required: field.field.required,
        placeholder: field.field.placeholder,
        options: field.field.options?.length || 0
      })),
      DEBUG_TIMESTAMP: new Date().toISOString()
    });

    // Debug completo per ogni campo custom
    console.log(`🚨 [ModularForm] CAMPI CUSTOM COMPLETI:`, {
      fields: customFields.map(field => ({
        id: field.id,
        label: field.field.label,
        customComponent: field.field.customComponent,
        type: field.field.type,
        required: field.field.required,
        placeholder: field.field.placeholder,
        options: field.field.options?.length || 0,
        defaultValue: field.field.defaultValue,
        validation: field.field.validation,
        formatter: field.field.formatter,
        icon: field.field.icon
      })),
      DEBUG_TIMESTAMP: new Date().toISOString()
    });

    // Debug espanso per ogni campo custom
    console.log(`🚨 [ModularForm] CAMPI CUSTOM ESPANSI DETTAGLIATI:`, {
      fields: customFields.map(field => ({
        id: field.id,
        label: field.field.label,
        customComponent: field.field.customComponent,
        type: field.field.type,
        required: field.field.required,
        placeholder: field.field.placeholder,
        options: field.field.options?.length || 0,
        defaultValue: field.field.defaultValue,
        validation: field.field.validation,
        formatter: field.field.formatter,
        icon: field.field.icon
      })),
      DEBUG_TIMESTAMP: new Date().toISOString()
    });

    // Debug espanso completo per ogni campo custom
    console.log(`🚨 [ModularForm] CAMPI CUSTOM ESPANSI COMPLETI:`, {
      fields: customFields.map(field => ({
        id: field.id,
        label: field.field.label,
        customComponent: field.field.customComponent,
        type: field.field.type,
        required: field.field.required,
        placeholder: field.field.placeholder,
        options: field.field.options?.length || 0,
        defaultValue: field.field.defaultValue,
        validation: field.field.validation,
        formatter: field.field.formatter,
        icon: field.field.icon
      })),
      DEBUG_TIMESTAMP: new Date().toISOString()
    });
  }

  // Funzione per aprire una sezione (temporaneamente non usata)
  // const openSection = useCallback((sectionId: string) => {
  //   setOpenSections(prev => new Set([...prev, sectionId]));
  // }, []);

  // Inizializza la navigazione tab (temporaneamente disabilitato per fix build)
  // TODO: Reimplementare useTabNavigation con signature corretta



  // Gestione cambio valori
  const handleValueChange = (fieldId: string, value: any) => {
    const newValues = { ...values, [fieldId]: value };
    setValues(newValues);
    
    // Salva i valori aggiornati in SessionStorage per persistenza
    const currentUserId = newValues.userId || 'current-user';
    const formDataToSave = {
      ...newValues,
      formType: formType
    };
    sessionStorage.setItem(`formData_${currentUserId}_${formType}`, JSON.stringify(formDataToSave));
    
    const allFields = currentConfig.groups.flatMap(group => 
      group.rows.flatMap(row => 
        row.columns.map(col => ({ id: col.id, field: col.field }))
      )
    );
    const newErrors = validateForm(allFields, newValues);
    setErrors(newErrors);
    
    onValuesChange?.(newValues);
    onErrorsChange?.(newErrors);
  };

  // Gestione tentativo di progressione (quando l'utente passa al campo email)
  const handleProgressionAttempt = (fieldId: string) => {
    // Se l'utente sta passando al campo email, segna che ha tentato di procedere
    if (fieldId === 'email') {
      setAttemptedProgression(prev => new Set([...prev, 'dati-azienda']));
    }
  };

  // Gestione cambio tipo form
  const handleFormTypeChange = (newType: FormType) => {
    console.log(`🔄 [ModularForm] Toggle Azienda/Privato: ${formType} → ${newType}`);
    
    const currentUserId = values.userId || 'current-user';
    
    // Salva i valori correnti in SessionStorage prima di cambiare tipo
    const formDataToSave = {
      ...values,
      formType: formType
    };
    sessionStorage.setItem(`formData_${currentUserId}_${formType}`, JSON.stringify(formDataToSave));
    console.log(`💾 [ModularForm] Salvato form data per ${formType} in SessionStorage`);
    
    setFormType(newType);
    
    // Salva stato toggle in SessionStorage per il cliente corrente
    sessionStorage.setItem(`toggleState_${currentUserId}`, newType);
    console.log(`💾 [ModularForm] Salvato in SessionStorage toggleState_${currentUserId}: ${newType}`);
    
    // Notifica il componente padre del cambio di tipo
    if (onFormTypeChange) {
      onFormTypeChange(newType);
    }
    
    // Prova a recuperare i valori salvati per il nuovo tipo da SessionStorage
    const savedFormDataKey = `formData_${currentUserId}_${newType}`;
    const savedFormData = sessionStorage.getItem(savedFormDataKey);
    
    const getDefaultValues = () => {
      // Extract default values from form configuration
      const configDefaults: Record<string, any> = {};
      
      currentConfig.groups.forEach(group => {
        group.rows.forEach(row => {
          row.columns.forEach(column => {
            if (column.field.defaultValue !== undefined) {
              configDefaults[column.id] = column.field.defaultValue;
            }
          });
        });
      });
      
      // Merge with hardcoded defaults
      return {
      // Valori di default sempre presenti
      indirizzo: { search: '', via: '', numeroCivico: '', citta: '', cap: '', provincia: '', paese: 'Italia' },
      prefissoNazione: 'IT',
      prefissoTelefono: '+39',
      referentePrincipalePrefissoFisso: '+39',
      referentePrincipalePrefissoCellulare: '+39',
      referenteSecondarioPrefissoFisso: '+39',
      referenteSecondariePrefissoCellulare: '+39',
      meccanicoPrefissoTelefono: '+39',
      referentePrivatoPrefissoFisso: '+39',
      referentePrivatoPrefissoCellulare: '+39',
      customer_type: newType === 'azienda' ? 'company' : 'individual',
      
      // Oggetti vuoti per sezioni complesse
      social: { whatsapp: false, telegram: false },
      telefonoSocial: { whatsapp: false, telegram: false },
      referentePrincipaleSocial: { whatsapp: false, telegram: false },
      referenteSecondarioSocial: { whatsapp: false, telegram: false },
      referentePrivatoSocial: { whatsapp: false, telegram: false },
      meccanicoIndirizzo: {
        via: '',
        numeroCivico: '',
        cap: '',
        citta: '',
        provincia: '',
        paese: 'Italia'
      },
      paymentMethods: {
        methods: [],
        preferences: { notes: '' },
        notes: ''
      },
      deliveryMethods: {
        methods: [],
        preferences: {
          preferredAddress: '',
          timeSlot: '',
          notes: ''
        },
        address: '',
        timeSlot: '',
        notes: ''
      },
      userId: currentUserId,
      // Sede preferita - preserva valore corrente quando si cambia tipo form
      sede_preferita: values.sede_preferita || '',
      // Apply defaults from form configuration
      ...configDefaults
      };
    };

    let finalValues;
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        // Rimuovi il campo formType e mantieni solo i dati del form
        const { formType: _, ...cleanData } = parsedData;
        finalValues = { ...getDefaultValues(), ...cleanData, userId: currentUserId };
        console.log(`🔄 [ModularForm] Recuperati valori salvati per ${newType} da SessionStorage`);
      } catch (error) {
        console.warn(`⚠️ [ModularForm] Errore parsing SessionStorage per ${newType}, usando defaults:`, error);
        finalValues = getDefaultValues();
      }
    } else {
      console.log(`📝 [ModularForm] Nessun dato salvato per ${newType}, usando defaults`);
      finalValues = getDefaultValues();
    }
    
    console.log(`🧹 [ModularForm] Impostando valori per tipo: ${newType}`);
    console.log('📋 [ModularForm] Valori finali:', finalValues);
    
    setValues(finalValues);
    setErrors({});
    setAttemptedProgression(new Set());
    
    // Propaga il cambiamento al componente padre
    onValuesChange?.(finalValues);
  };

  // Gestione submit
  const handleSubmit = async (e: React.FormEvent) => {
    console.log('🔍 [ModularForm] handleSubmit chiamato');
    console.log('🔍 [ModularForm] Valori del form:', values);
    e.preventDefault();

    // IMPORTANTE: Cattura il riferimento al form PRIMA di qualsiasi operazione asincrona
    // perché React ricicla gli eventi sintetici e e.currentTarget diventa null dopo await
    const formElement = e.currentTarget as HTMLFormElement;

    // Validazione prima del submit
    const allFields = currentConfig.groups.flatMap(group =>
      group.rows.flatMap(row =>
        row.columns.map(col => ({ id: col.id, field: col.field }))
      )
    );
    const formErrors = validateForm(allFields, values);
    console.log('🔍 [ModularForm] Errori validazione:', formErrors);
    console.log('🔍 [ModularForm] skipRequiredValidation:', skipRequiredValidation);

    // Separa errori "required" da altri errori di formato
    const requiredErrors: Record<string, string> = {};
    const otherErrors: Record<string, string> = {};

    Object.entries(formErrors).forEach(([key, msg]) => {
      if (msg.toLowerCase().includes('obbligatorio') || msg.toLowerCase().includes('required')) {
        requiredErrors[key] = msg;
      } else {
        otherErrors[key] = msg;
      }
    });

    // Gli errori di formato (non required) bloccano sempre
    if (Object.keys(otherErrors).length > 0) {
      console.log('❌ [ModularForm] Errori di formato:', otherErrors);
      setErrors(formErrors);
      onErrorsChange?.(formErrors);
      return;
    }

    // Se ci sono errori required E NON skip validation, blocca
    if (Object.keys(requiredErrors).length > 0 && !skipRequiredValidation) {
      console.log('❌ [ModularForm] Form non valido, errori required:', requiredErrors);
      setErrors(formErrors);
      onErrorsChange?.(formErrors);
      return;
    }

    // Se skipRequiredValidation e ci sono campi mancanti, chiedi conferma
    if (Object.keys(requiredErrors).length > 0 && skipRequiredValidation && onMissingRequiredFields) {
      // Estrai nomi campi mancanti dai field config
      const missingFieldLabels: string[] = Object.keys(requiredErrors).map(fieldId => {
        const field = allFields.find(f => f.id === fieldId);
        const label = field?.field?.label;
        // Converti ReactNode a string se necessario
        return typeof label === 'string' ? label : fieldId;
      });

      console.log('⚠️ [ModularForm] Campi required mancanti:', missingFieldLabels);

      // Chiedi conferma all'utente
      const shouldProceed = await onMissingRequiredFields(missingFieldLabels);
      if (!shouldProceed) {
        console.log('❌ [ModularForm] Utente ha annullato per campi mancanti');
        return;
      }
      console.log('✅ [ModularForm] Utente ha confermato di procedere senza campi required');
    }

    // Previene submit multipli
    const submitButton = formElement?.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Salvando...';

      // Riabilita il pulsante dopo 1 secondo
      setTimeout(() => {
        submitButton.disabled = false;
        submitButton.textContent = submitButtonText;
      }, 1000);
    }

    if (onSubmit) {
      console.log('✅ [ModularForm] Form valido, chiamata onSubmit con valori:', values);
      try {
        const result = onSubmit(values);
        if (result instanceof Promise) {
          result.catch(error => {
            console.error('❌ [ModularForm] Errore durante onSubmit:', error);
            // Riabilita il pulsante in caso di errore
            if (submitButton) {
              submitButton.disabled = false;
              submitButton.textContent = submitButtonText;
            }
          });
        }
      } catch (error) {
        console.error('❌ [ModularForm] Errore durante onSubmit:', error);
        // Riabilita il pulsante in caso di errore
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = submitButtonText;
        }
      }
    } else {
      console.log('⚠️ [ModularForm] Nessuna funzione onSubmit fornita');
      // Riabilita il pulsante se non c'è onSubmit
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = submitButtonText;
      }
    }
  };



  // Animazioni
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.2 }
    }
  };

  // @ts-ignore - Variants Framer Motion per uso futuro
  const _formVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4 }
    },
    exit: {
      opacity: 0,
      x: -50,
      transition: { duration: 0.3 }
    }
  };

  // Debug render - mostra stato values corrente
  console.log('🖼️ [ModularForm] RENDER - values.email:', values.email, 'values.ragioneSociale:', values.ragioneSociale);
  console.log('🖼️ [ModularForm] RENDER - Object.keys(values).length:', Object.keys(values).length);

  return (
    <div id="modular-form-container" className={`w-full max-w-4xl mx-auto ${className}`}>
      {/* Header con switch + contenuto opzionale a sinistra */}
      {showTypeSwitch && (
        <motion.div
          className="mb-8 p-6 bg-zinc-800 rounded-lg shadow-sm border border-zinc-700"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Layout a 2 colonne se c'è contenuto extra (foto) */}
          <div className={`flex ${typeSwitchLeftContent ? 'flex-col lg:flex-row gap-6 items-start' : 'flex-col'}`}>
            {/* Colonna sinistra: Contenuto extra (es. foto profilo) */}
            {typeSwitchLeftContent && (
              <div className="lg:w-2/5 flex-shrink-0">
                {typeSwitchLeftContent}
              </div>
            )}

            {/* Colonna destra: Switch Tipo Cliente + QR code sotto */}
            <div className={`${typeSwitchLeftContent ? 'lg:w-3/5' : 'w-full'}`}>
              <ModernFormTypeSwitch
                currentType={formType}
                onTypeChange={handleFormTypeChange}
                disabled={readOnly}
                showConfirmation={true}
              />

              {/* QR code cliente sotto il commutatore Azienda/Privato */}
              {typeSwitchBottomContent && (
                <div className="mt-4">
                  {typeSwitchBottomContent}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Form con animazione di transizione */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.form
          ref={formRef}
          id="modular-form"
          key={readOnly ? 'readonly-form' : formType}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Titolo form - RIMOSSO per evitare doppioni */}
          {/*
          <motion.div
            className="text-center mb-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <h1 className="text-3xl font-bold text-white mb-2">
              {currentConfig.title}
            </h1>
            <p className="text-zinc-300">
              {currentConfig.description}
            </p>
          </motion.div>
          */}

          {/* Gruppi del form */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {currentConfig.groups.map((group, idx, arr) => {
              const isGroupValid = (g: FormGroupType) => {
                let total = 0, valid = 0, hasNonObbligError = false;
                const requiredFields: Array<{id: string, value: any, error: any}> = [];

                g.rows.forEach((row: FormRow) => {
                  row.columns.forEach((col: FormColumn) => {
                    const v = values[col.id];
                    const err = errors[col.id];
                    if (col.field.required) {
                      total++;
                      if (v && !err) valid++;
                      requiredFields.push({id: col.id, value: v, error: err});
                    }
                    if (!col.field.required && err) hasNonObbligError = true;
                  });
                });

                // console.log('🔧 ModularForm - Validazione gruppo:', g.id, {
                //   total,
                //   valid,
                //   hasNonObbligError,
                //   requiredFields,
                //   result: total > 0 && valid === total && !hasNonObbligError
                // });

                return total > 0 && valid === total && !hasNonObbligError;
              };

              // Se skipRequiredValidation è true, tutte le sezioni sono abilitate
              // (per permettere la modifica di qualsiasi campo)
              let enabled = true;

              if (!skipRequiredValidation) {
                // La prima sezione è sempre abilitata
                enabled = idx === 0;

                // Se non è la prima sezione, controlla se la sezione precedente è valida
                if (idx > 0) {
                  // Trova la sezione precedente valida (che ha campi obbligatori)
                  let prevValidSection = idx - 1;
                  while (prevValidSection >= 0) {
                    const prevGroup = arr[prevValidSection];
                    const prevGroupRequiredFields = prevGroup.rows.reduce((total, row) => {
                      return total + row.columns.filter(col => col.field.required).length;
                    }, 0);

                    // Se la sezione precedente ha campi obbligatori, verifica se è valida
                    if (prevGroupRequiredFields > 0) {
                      enabled = isGroupValid(prevGroup);
                      break;
                    }
                    // Se non ha campi obbligatori, passa alla sezione precedente
                    prevValidSection--;
                  }

                  // Se non troviamo sezioni precedenti con campi obbligatori, abilita
                  if (prevValidSection < 0) {
                    enabled = true;
                  }
                }

                // Sezione speciale per referente secondario - sempre abilitata se opzionale
                if (group.id === 'referente-secondario') {
                  const hasRequiredFields = group.rows.some(row =>
                    row.columns.some(col => col.field.required)
                  );
                  if (!hasRequiredFields) {
                    enabled = true; // Sezione completamente opzionale, sempre abilitata
                  }
                }
              }

              // Debug per verificare se i campi custom sono presenti nel gruppo
              const customFieldsInGroup = group.rows?.flatMap(row => 
                row.columns?.filter(col => col.field.type === 'custom') || []
              ) || [];
              
              if (customFieldsInGroup.length > 0) {
                console.log(`🚨 [ModularForm] GRUPPO CON CAMPI CUSTOM: ${group.id}`, {
                  groupTitle: group.title,
                  customFields: customFieldsInGroup.map(f => ({
                    id: f.id,
                    label: f.field.label,
                    customComponent: f.field.customComponent
                  })),
                  DEBUG_TIMESTAMP: new Date().toISOString()
                });
              }

              // Debug per paymentMethods e deliveryMethods
              if (group.id === 'modalita-pagamento' || group.id === 'modalita-consegna') {
                console.log(`🔍 [ModularForm] Passando values a FormGroup ${group.id}:`, {
                  paymentMethods: values.paymentMethods,
                  deliveryMethods: values.deliveryMethods,
                  allKeys: Object.keys(values)
                });
              }

              return (
                <div key={group.id} data-section-id={group.id}>
                  <FormGroup
                    group={group}
                    values={values}
                    errors={errors}
                    onValueChange={handleValueChange}
                    onProgressionAttempt={handleProgressionAttempt}
                    userPermissions={userPermissions}
                    enabled={enabled}
                    forceOpen={openSections.has(group.id)}
                    attemptedProgression={attemptedProgression}
                    readOnly={readOnly}
                  />
                </div>
              );
            })}
          </motion.div>

          {/* Pulsanti azione - esclusi dalla navigazione tab */}
          {!readOnly && !hideSubmitButton && (
            <motion.div
              className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-zinc-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {!hideResetButton && (
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => {
                    setValues({});
                    setErrors({});
                    setAttemptedProgression(new Set());
                  }}
                  className="px-6 py-2 text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 transition-colors"
                >
                  Reset
                </button>
              )}
              <button
                type="submit"
                tabIndex={-1}
                disabled={hasBlockingErrors}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  hasBlockingErrors
                    ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {submitButtonText}
              </button>
            </motion.div>
          )}
        </motion.form>
      </AnimatePresence>

      {/* Debug panel (solo in sviluppo) */}
      {import.meta.env.DEV && (
        <motion.div
          className="mt-8 p-4 bg-zinc-900 rounded-lg border border-zinc-700"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h3 className="text-sm font-semibold text-zinc-300 mb-2">
            Debug - Valori Form ({formType})
          </h3>
          <pre className="text-xs text-zinc-400 overflow-auto">
            {JSON.stringify(values, null, 2)}
          </pre>
        </motion.div>
      )}

      {/* Pulsante salva fisso floating - nascosto in modalità readOnly o quando hideSubmitButton */}
      {!readOnly && !hideSubmitButton && (
        <div className="fixed bottom-6 right-24 z-50">
          <button
            type="button"
            onClick={() => {
              // Simula submit del form
              const form = document.querySelector('form');
              if (form) {
                const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                form.dispatchEvent(submitEvent);
              }
            }}
            disabled={hasBlockingErrors}
            className={`
              px-6 py-3 rounded-full font-semibold text-sm
              shadow-lg hover:shadow-xl transition-all duration-200
              ${hasBlockingErrors
                ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              {submitButtonText}
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default ModularForm; 